import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { IAgent, IAgents, IAgentsResponse, IMyAgent } from '../../../interfaces/i-agents';
import { AgentTableComponent } from '../agent-table/agent-table.component';
import { AgentService } from '../services/agent.service';
import { SpinnerService } from "../../../shared/spinner/spinner.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { AlertEnum } from "../../../shared/alert/alert.enum";
import { ISensitivityLabel } from "../../../interfaces/i-sensitivity-label";

@Component({
  selector: 'app-agent-manager',
  imports: [
    CommonModule,
    AgentTableComponent,
  ],
  templateUrl: './agent-manager.component.html',
  styleUrl: './agent-manager.component.scss'
})
export class AgentManagerComponent {
  gridView = signal<IMyAgent[]>([]);
  agentsLoaded = signal<boolean>(false);

  #apiService = inject(ApiService);
  #agentService = inject(AgentService);
  #spinnerService = inject(SpinnerService);
  #alertService = inject(AlertService);

  constructor() {
    this.#spinnerService.show();
    this.#apiService.getAgents().subscribe( {
      next: (data: IAgentsResponse) => {
        const updatedData = this.#agentService.convertAgentsToArray(data);
        const updatedDataAgents = {
          ...updatedData,
          agents: updatedData.agents
            .filter(item => item.details) // Filter out items with null or undefined details
            .map(item => ({
              ...item,
              details: this.#agentService.convertAgentArraysToSting(item.details)
            }))
        };
        this.#agentService.agents.set(updatedData);
        const gridData = this.#extractDetails(updatedDataAgents);

        this.gridView.set(gridData);
        this.agentsLoaded.set(true);
      },
      error: (error: any) => {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
          message: `Failed to get all agents.`,
          type: AlertEnum.error,
        });
      },
      complete: () => {
        this.#spinnerService.hide();
      }
    });
  }

  #extractDetails(agentsObject: IAgents): IAgent['details'][] {
    return agentsObject.agents.map(agent => {
      let sensitivityLabels: ISensitivityLabel[] = [];
      if (Object.hasOwn(agent.details, 'sensitivityLabels')) {
        if (agent.details.sensitivityLabels && agent.details.sensitivityLabels.length > 0) {
          sensitivityLabels = agent.details.sensitivityLabels.map(label => {
            return {
              ...label,
              guid: label.guid ?? ''
            };
          });
        }
      }

      return {
        ...agent.details,
        groups: agent.details.groups ?? [],
        sensitivityLabels: sensitivityLabels,
        isUniversal: agent.isUniversal,
        isDisabled: agent.isDisabled
      };
    });
  }
}
